import { Button } from "../components/Button";
import { ContentMessage } from "../components/ContentMessage";
import { Icon } from "../components/Icon";
import { Input } from "../components/Input";
import { PopoverContent, PopoverRoot, PopoverTrigger, } from "../components/Popover";
import { ScrollArea, ScrollBar } from "../components/ScrollArea";
import { Spinner } from "../components/Spinner";
import { ListCheckIcon, MagnifyingGlassIcon, XMarkIcon, } from "../icons/app";
import { cn } from "../lib/utils";
import React, { forwardRef, useEffect, useRef, useState, } from "react";
export const SearchInput = forwardRef(({ placeholder = "Search", value, onChange, onKeyDown, onFocus, onBlur, name, disabled = false, isLoading = false, className, }, ref) => {
    const clearInputField = () => {
        onChange("");
    };
    return (React.createElement("div", { className: cn("s-relative", className) },
        React.createElement(Input, { type: "text", name: name, autoComplete: "off", placeholder: placeholder, value: value, onChange: (e) => {
                onChange(e.target.value);
            }, onFocus: onFocus, onBlur: onBlur, onKeyDown: onKeyDown, disabled: disabled, ref: ref }),
        React.createElement("div", { className: "s-absolute s-inset-y-0 s-right-0 s-flex s-items-center s-pr-1" }, isLoading ? (React.createElement("div", { className: "s-px-1" },
            React.createElement(Spinner, { size: "xs" }))) : value ? (React.createElement(Button, { icon: XMarkIcon, variant: "ghost", size: "xs", onClick: clearInputField })) : (React.createElement("div", { className: cn("s-px-2", disabled
                ? "s-text-muted-foreground dark:s-text-muted-foreground-night"
                : "s-text-foreground dark:s-text-foreground-night") },
            React.createElement(Icon, { visual: MagnifyingGlassIcon, size: "xs", className: "s-text-muted-foreground dark:s-text-muted-foreground-night" }))))));
});
SearchInput.displayName = "SearchInput";
const MAX_HEIGHT_CLASSES = {
    sm: "s-max-h-48",
    md: "s-max-h-72",
    lg: "s-max-h-96",
    xl: "s-max-h-[40rem]",
};
function BaseSearchInputWithPopover({ items, renderItem, onItemSelect, onSelectAll, contentClassName, className, open, onOpenChange, value, onChange, mountPortal, mountPortalContainer, noResults, isLoading, contentMessage, displayItemCount = false, totalItems, stickyTopContent, stickyBottomContent, availableHeight = false, maxHeight = "md", ...searchInputProps }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const itemRefs = useRef([]);
    const showHeader = Boolean(stickyTopContent) ||
        (items.length > 0 && (displayItemCount || onSelectAll));
    const showBottom = Boolean(stickyBottomContent);
    useEffect(() => {
        itemRefs.current = new Array(items.length).fill(null);
    }, [items.length]);
    useEffect(() => {
        setSelectedIndex(0);
    }, [items]);
    const handleKeyDown = (e) => {
        if (!open || !items.length) {
            return;
        }
        switch (e.key) {
            case "ArrowUp":
                e.preventDefault();
                setSelectedIndex((current) => {
                    var _a;
                    const newIndex = (current - 1 + items.length) % items.length;
                    (_a = itemRefs.current[newIndex]) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: "nearest" });
                    return newIndex;
                });
                break;
            case "ArrowDown":
                e.preventDefault();
                setSelectedIndex((current) => {
                    var _a;
                    const newIndex = (current + 1) % items.length;
                    (_a = itemRefs.current[newIndex]) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: "nearest" });
                    return newIndex;
                });
                break;
            case "Enter":
                e.preventDefault();
                onOpenChange(false);
                if (items[selectedIndex] && onItemSelect) {
                    onItemSelect(items[selectedIndex]);
                }
                break;
        }
    };
    return (React.createElement(PopoverRoot, { modal: false, open: open, onOpenChange: onOpenChange },
        React.createElement(PopoverTrigger, { asChild: true },
            React.createElement(SearchInput, { ref: ref, className: cn("s-w-full", className), value: value, onChange: (newValue) => {
                    onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
                    if (newValue && !open) {
                        onOpenChange(true);
                    }
                }, ...searchInputProps, onKeyDown: handleKeyDown, "aria-expanded": open, "aria-haspopup": "listbox", "aria-controls": "search-popover-content" })),
        React.createElement(PopoverContent, { className: cn("s-w-[--radix-popover-trigger-width] s-rounded-lg s-border s-bg-background s-shadow-lg dark:s-bg-background-night", availableHeight &&
                "s-max-h-[var(--radix-popover-content-available-height)] s-overflow-hidden", contentClassName), sideOffset: 0, fullWidth: true, align: "start", id: "search-popover-content", onOpenAutoFocus: (e) => e.preventDefault(), onCloseAutoFocus: (e) => e.preventDefault(), onInteractOutside: () => onOpenChange(false), mountPortal: mountPortal, mountPortalContainer: mountPortalContainer },
            React.createElement("div", { className: cn("s-flex s-flex-col s-overflow-hidden s-rounded-lg", availableHeight && "s-max-h-full") },
                showHeader && (React.createElement("div", { className: cn("s-z-10 s-flex s-shrink-0 s-items-center s-justify-between s-gap-2 s-border-b s-border-border s-bg-background s-p-2 dark:s-border-border-night dark:s-bg-background-night"), onMouseDown: (e) => e.preventDefault() },
                    React.createElement("div", { className: "s-flex s-flex-1 s-items-center s-gap-2" },
                        stickyTopContent,
                        displayItemCount && items.length > 0 && (React.createElement("span", { className: "s-text-sm s-text-gray-500" },
                            items.length,
                            " search results",
                            totalItems && ` (out of ${totalItems})`,
                            "."))),
                    onSelectAll && items.length > 0 && (React.createElement(Button, { variant: "ghost", size: "xs", onClick: onSelectAll, label: "Select all", icon: ListCheckIcon })))),
                React.createElement(ScrollArea, { className: cn("s-flex s-min-h-0 s-flex-1 s-flex-col", availableHeight
                        ? "s-max-h-[calc(var(--radix-popover-content-available-height)-12px)]"
                        : MAX_HEIGHT_CLASSES[maxHeight]), hideScrollBar: true },
                    React.createElement("div", { role: "listbox", className: "s-flex s-flex-col" }, items.length > 0 ? (items.map((item, index) => (React.createElement("div", { key: index, ref: (el) => (itemRefs.current[index] = el), onClick: () => {
                            onOpenChange(false);
                            onItemSelect === null || onItemSelect === void 0 ? void 0 : onItemSelect(item);
                        } }, renderItem(item, selectedIndex === index))))) : isLoading ? (React.createElement("div", { className: "s-flex s-justify-center s-py-8" },
                        React.createElement(Spinner, { variant: "dark", size: "md" }))) : (React.createElement("div", { className: "s-p-4 s-text-center s-text-sm s-italic s-text-muted-foreground dark:s-text-muted-foreground-night" }, noResults !== null && noResults !== void 0 ? noResults : ""))),
                    React.createElement(ScrollBar, { className: "s-py-0" })),
                showBottom && (React.createElement("div", { className: cn("s-z-10 s-hidden s-shrink-0 s-items-center s-justify-between s-gap-2 s-border-t s-border-border s-bg-background s-p-2 dark:s-border-border-night dark:s-bg-background-night sm:s-flex") }, stickyBottomContent)),
                contentMessage && (React.createElement("div", { className: "s-p-1" },
                    React.createElement(ContentMessage, { ...contentMessage })))))));
}
export const SearchInputWithPopover = forwardRef(BaseSearchInputWithPopover);
//# sourceMappingURL=SearchInput.js.map